import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

import Iconify from 'src/components/Iconify';
import PageContainerHeader from 'src/components/PageContainerHeader';
import { Input } from 'src/@/components/ui/input';
import { Card } from 'src/@/components/ui/card';
import { Button } from 'src/@/components/ui/button';
import { useToast } from 'src/@/components/ui/use-toast';

import campanhasService from 'src/services/campanhasService';
import {
    CAMPANHAS_DESIGN as D,
    configStatusCampanha,
    configStatusJob,
    configTipoEnvio,
    rotuloSmsStatus,
} from '../Campanhas.design';

/** Cores alinhadas ao bloco "Taxa de Entrega" de Consumo. */
const STATUS_CORES = {
    Enviado: '#f59e0b',
    Entregue: '#3b82f6',
    Lido: '#10b981',
    Erro: '#ef4444',
    Pendente: '#6b7280',
    Cancelado: '#9ca3af',
};

const pctDe = (parte, total) => {
    if (!total) return '0.0';
    return ((parte / total) * 100).toFixed(1);
};

const POR_PAGINA = 50;
const INTERVALO_POLL_MS = 60000;
const INTERVALO_POLL_OCIOSO_MS = 5 * 60 * 1000;
const INTERVALO_POLL_LONGO_MS = 15 * 60 * 1000;
const OCIOSO_APOS_MS = 10 * 60 * 1000;
const LONGO_APOS_MS = 30 * 60 * 1000;
const STATUS_TERMINAIS = ['finalizado', 'erro'];

const intervaloPollPorOciosidade = (ociosoMs) => {
    if (ociosoMs >= LONGO_APOS_MS) return INTERVALO_POLL_LONGO_MS;
    if (ociosoMs >= OCIOSO_APOS_MS) return INTERVALO_POLL_OCIOSO_MS;
    return INTERVALO_POLL_MS;
};

const EVENTOS = {
    criada:    { label: 'Campanha criada',        icon: 'mdi:plus-circle-outline',  classe: 'bg-gray-100 dark:bg-[#383838] text-gray-600 dark:text-[#ABABAD]' },
    agendada:  { label: 'Destinatários agendados', icon: 'mdi:calendar-clock',      classe: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    iniciada:  { label: 'Primeiro envio',         icon: 'mdi:send-outline',         classe: 'bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]' },
    entregue:  { label: 'Confirmações de entrega', icon: 'mdi:check-all',           classe: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' },
    falhas:    { label: 'Falhas de envio',        icon: 'mdi:alert-circle-outline', classe: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' },
    concluida: { label: 'Último envio',           icon: 'mdi:flag-checkered',       classe: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' },
};

const FILTROS_STATUS = [
    { valor: '', label: 'Todos' },
    { valor: 'agendado', label: 'Agendados' },
    { valor: 'enviado', label: 'Enviados' },
    { valor: 'erro', label: 'Falhas' },
    { valor: 'cancelado', label: 'Cancelados' },
];

const formatarDataHora = (valor) => {
    if (!valor) return '—';
    return new Date(valor).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

const formatarHora = (valor) => {
    if (!valor) return '—';
    return new Date(valor).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

/** Sem ano: usado na coluna de horario da lista, onde o ano so repete ruido. */
const formatarDiaHora = (valor) => {
    if (!valor) return '—';
    return new Date(valor).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

/** "há 12s" — deixa explicito o quao defasado o painel esta. */
const formatarDesde = (segundos) => {
    if (segundos < 60) return `há ${segundos}s`;
    const min = Math.floor(segundos / 60);
    if (min < 60) return `há ${min}min`;
    return `há ${Math.floor(min / 60)}h`;
};

export default function CampanhaDetalhes() {
    const { campanhaId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [busca, setBusca] = useState('');
    const [expandido, setExpandido] = useState(null);
    const [atualizadoHa, setAtualizadoHa] = useState(0);
    const [atualizando, setAtualizando] = useState(false);

    // Refs para o polling nao se re-registrar a cada interacao.
    const paginaRef = useRef(pagina);
    paginaRef.current = pagina;
    const filtroRef = useRef(filtroStatus);
    filtroRef.current = filtroStatus;
    const buscaRef = useRef(busca);
    buscaRef.current = busca;

    const carregar = useCallback(
        async ({ silencioso = false } = {}) => {
            if (!campanhaId) return;
            if (!silencioso) setLoading(true);
            try {
                const resposta = await campanhasService.listarJobs(campanhaId, {
                    page: 1,
                    // Acumula as paginas ja abertas para o "carregar mais" nao ser
                    // desfeito pelo poll.
                    limit: POR_PAGINA * paginaRef.current,
                    status: filtroRef.current || undefined,
                    q: buscaRef.current || undefined,
                });
                setDados(resposta.data);
                setErro(null);
                setAtualizadoHa(0);
            } catch (err) {
                if (!silencioso) setErro('Não foi possível carregar o acompanhamento');
            } finally {
                if (!silencioso) setLoading(false);
            }
        },
        [campanhaId]
    );

    const handleRecarregar = async () => {
        setAtualizando(true);
        try {
            await carregar({ silencioso: true });
        } finally {
            setAtualizando(false);
        }
    };

    useEffect(() => {
        carregar();
    }, [carregar]);

    // Recarrega ao mudar filtro/pagina/busca (sem spinner: e interacao do usuario).
    // A busca entra com debounce para nao disparar uma requisicao por tecla.
    useEffect(() => {
        if (!dados) return undefined;
        const timer = setTimeout(() => carregar({ silencioso: true }), busca ? 400 : 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroStatus, pagina, busca]);

    // O status_envio persistido pode estar defasado: o CampaignsSender so o
    // recalcula quando processa jobs, entao uma campanha 100% enviada pode seguir
    // "agendado" no banco. Deriva o status dos contadores (mesma logica do
    // atualizarStatusCampanha do sender) — evita badge errado, "previsao de
    // termino" no passado e polling infinito de campanha ja concluida.
    const statusEfetivo = useMemo(() => {
        const summaryAtual = dados?.summary;
        if (!summaryAtual || !summaryAtual.total) return dados?.resumo?.status_envio;
        const pendentes = (summaryAtual.agendado || 0) + (summaryAtual.processando || 0);
        const enviados = summaryAtual.enviado || 0;
        const erros = summaryAtual.erro || 0;
        if (pendentes > 0) return enviados + erros > 0 ? 'em andamento' : 'agendado';
        if (enviados > 0) return 'finalizado';
        if (erros > 0) return 'erro';
        return 'finalizado';
    }, [dados]);

    const emAndamento = dados ? !STATUS_TERMINAIS.includes(statusEfetivo) : false;

    // Polling e o mecanismo primario: o websocket do painel e bloqueado em alguns
    // navegadores. Pausa com a aba em background. Se a pagina ficar aberta por
    // horas sem interacao, o intervalo sobe (1min -> 5min -> 15min) para nao
    // martelar a API; volta a 1min no primeiro clique/scroll/tecla.
    useEffect(() => {
        if (!emAndamento) return undefined;

        let timeoutId;
        let ultimaInteracao = Date.now();

        const marcarInteracao = () => {
            ultimaInteracao = Date.now();
        };

        const agendar = () => {
            const delay = intervaloPollPorOciosidade(Date.now() - ultimaInteracao);
            timeoutId = setTimeout(() => {
                if (document.visibilityState === 'visible') {
                    carregar({ silencioso: true });
                }
                agendar();
            }, delay);
        };

        const aoVisivel = () => {
            if (document.visibilityState !== 'visible') return;
            marcarInteracao();
            carregar({ silencioso: true });
            clearTimeout(timeoutId);
            agendar();
        };

        window.addEventListener('pointerdown', marcarInteracao);
        window.addEventListener('keydown', marcarInteracao);
        window.addEventListener('scroll', marcarInteracao, { passive: true });
        document.addEventListener('visibilitychange', aoVisivel);
        agendar();

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('pointerdown', marcarInteracao);
            window.removeEventListener('keydown', marcarInteracao);
            window.removeEventListener('scroll', marcarInteracao);
            document.removeEventListener('visibilitychange', aoVisivel);
        };
    }, [emAndamento, carregar]);

    // Contador do selo "atualizado há Xs".
    useEffect(() => {
        const id = setInterval(() => setAtualizadoHa((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const handleCancelar = async (job) => {
        try {
            await campanhasService.cancelarJob(job._id);
            toast({ title: 'Envio cancelado', description: job.nome_cliente || job.numero_cliente });
            carregar({ silencioso: true });
        } catch (err) {
            toast({
                title: 'Erro',
                description: err?.mensagem || 'Não foi possível cancelar este envio',
                variant: 'destructive',
            });
        }
    };

    const summary = dados?.summary || {};
    const resumo = dados?.resumo || {};
    const jobs = dados?.jobs || [];
    const ehSms = resumo.tipo_envio === 'sms';

    const cfgStatus = dados ? configStatusCampanha(statusEfetivo) : null;
    const cfgTipo = configTipoEnvio(resumo.tipo_envio);
    const cfgPublico = D.tipoPublico[resumo.tipo];

    // Barra de composicao: lido/entregue herdam o verde dos chips, enviado herda o
    // violeta da barra da listagem — "enviado" e sempre "enviado ainda sem
    // confirmacao" (entregue e subconjunto de enviado; lido ⊂ entregue). Vale para
    // SMS (Comtele) e WhatsApp (Meta) — ambos alimentam summary.lido/entregue.
    const segmentosBarra = useMemo(() => {
        const total = summary.total || 0;
        if (!total) return [];
        const lido = summary.lido || 0;
        const entregue = Math.max(0, (summary.entregue || 0) - lido);
        return [
            { key: 'lido',        valor: lido,                                classe: 'bg-emerald-500',                   label: 'Lidos' },
            { key: 'entregue',    valor: entregue,                            classe: 'bg-green-500',                     label: 'Entregues' },
            { key: 'enviado',     valor: (summary.enviado || 0) - entregue - lido, classe: 'bg-[#7C3AED]',                 label: 'Enviados, aguardando confirmação' },
            { key: 'processando', valor: summary.processando || 0,            classe: 'bg-blue-400 animate-pulse',        label: 'Enviando agora' },
            { key: 'erro',        valor: summary.erro || 0,                   classe: 'bg-red-400',                       label: 'Falhas' },
            { key: 'cancelado',   valor: summary.cancelado || 0,              classe: 'bg-gray-300 dark:bg-gray-600',     label: 'Cancelados' },
            { key: 'agendado',    valor: summary.agendado || 0,               classe: 'bg-gray-200 dark:bg-[#383838]',    label: 'Agendados' },
        ]
            .filter((s) => s.valor > 0)
            .map((s) => ({ ...s, pct: (s.valor / total) * 100 }));
    }, [summary]);

    const pctEnviado = summary.total ? Math.round(((summary.enviado || 0) / summary.total) * 100) : 0;

    // Pizza com fatias exclusivas (Lido ⊂ Entregue ⊂ Enviado) + taxas cumulativas
    // no estilo do modal de Consumo (enviado / entregue / lido / erro).
    const chartDataStatus = useMemo(() => {
        const lido = summary.lido || 0;
        const entregueSo = Math.max(0, (summary.entregue || 0) - lido);
        const enviadoSo = Math.max(0, (summary.enviado || 0) - (summary.entregue || 0));
        const pendente = (summary.agendado || 0) + (summary.processando || 0);
        return [
            { name: 'Lido', value: lido, fill: STATUS_CORES.Lido },
            { name: 'Entregue', value: entregueSo, fill: STATUS_CORES.Entregue },
            { name: 'Enviado', value: enviadoSo, fill: STATUS_CORES.Enviado },
            { name: 'Erro', value: summary.erro || 0, fill: STATUS_CORES.Erro },
            { name: 'Pendente', value: pendente, fill: STATUS_CORES.Pendente },
            { name: 'Cancelado', value: summary.cancelado || 0, fill: STATUS_CORES.Cancelado },
        ].filter((item) => item.value > 0);
    }, [summary]);

    const deliveryRates = useMemo(() => {
        const total = summary.total || 0;
        return {
            sent: pctDe(summary.enviado || 0, total),
            delivered: pctDe(summary.entregue || 0, total),
            read: pctDe(summary.lido || 0, total),
            error: pctDe(summary.erro || 0, total),
        };
    }, [summary]);

    const timeline = useMemo(() => {
        if (!dados) return [];
        const eventos = [];
        if (resumo.criada_em) {
            eventos.push({
                key: 'criada',
                detalhes: [formatarDataHora(resumo.criada_em), resumo.criada_por].filter(Boolean),
            });
        }
        if (summary.total) {
            eventos.push({
                key: 'agendada',
                detalhes: [
                    `${summary.total} ${summary.total === 1 ? 'destinatário' : 'destinatários'}`,
                    resumo.primeiro_disparo_em ? `a partir de ${formatarDataHora(resumo.primeiro_disparo_em)}` : null,
                ].filter(Boolean),
            });
        }
        if (resumo.primeiro_envio_em) {
            eventos.push({ key: 'iniciada', detalhes: [formatarDataHora(resumo.primeiro_envio_em)] });
        }
        if (summary.entregue > 0) {
            eventos.push({
                key: 'entregue',
                detalhes: [
                    summary.lido > 0
                        ? `${summary.entregue} de ${summary.enviado} no aparelho · ${summary.lido} lidos`
                        : `${summary.entregue} de ${summary.enviado} confirmados no aparelho`,
                ],
            });
        }
        if (summary.erro > 0) {
            eventos.push({
                key: 'falhas',
                detalhes: [`${summary.erro} ${summary.erro === 1 ? 'envio falhou' : 'envios falharam'}`],
            });
        }
        if (!emAndamento && resumo.ultimo_envio_em) {
            eventos.push({ key: 'concluida', detalhes: [formatarDataHora(resumo.ultimo_envio_em)] });
        }
        return eventos;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados, emAndamento]);

    const comErro = jobs.filter((j) => j.status === 'erro');

    return (
        <div className={D.detalhes.pagina}>
            <PageContainerHeader
                titleSection={
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/marketing/campanhas')}
                            className={D.detalhes.voltar}
                            title="Voltar para campanhas"
                        >
                            <Iconify icon="eva:arrow-back-outline" width={18} />
                        </button>
                        <div className={D.page.iconBox}>
                            <Iconify icon={cfgTipo.icon} width={18} className={D.page.icon} />
                        </div>
                        <h3 className={`${D.detalhes.titulo} truncate`}>{resumo.nome || 'Acompanhamento'}</h3>
                        {cfgStatus && (
                            <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md flex-shrink-0 ${cfgStatus.bg} ${cfgStatus.text}`}
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${cfgStatus.dot}`} />
                                {cfgStatus.label}
                            </span>
                        )}
                    </div>
                }
                rightSection={
                    <div className="flex items-center gap-3">
                        <span className={`hidden sm:inline ${D.detalhes.atualizado}`}>
                            {`Atualizado ${formatarDesde(atualizadoHa)}`}
                        </span>
                        <button
                            type="button"
                            onClick={handleRecarregar}
                            disabled={atualizando}
                            className={D.detalhes.recarregar}
                            title="Recarregar acompanhamento"
                        >
                            <Iconify
                                icon={atualizando ? 'eos-icons:loading' : 'eva:refresh-outline'}
                                width={16}
                            />
                            Atualizar
                        </button>
                    </div>
                }
            />

            <div className={D.detalhes.content}>
                {loading && (
                    <Card className={D.estado.loadingCard}>
                        <div className={D.estado.loadingBox}>
                            <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
                            <p className={D.estado.loadingTexto}>Carregando acompanhamento...</p>
                        </div>
                    </Card>
                )}

                {!loading && erro && (
                    <Card className={D.estado.loadingCard}>
                        <div className={D.estado.vazioBox}>
                            <div className={D.estado.vazioIcone}>
                                <Iconify icon="mdi:alert-circle-outline" width={28} className="text-[#7C3AED]" />
                            </div>
                            <h3 className={D.estado.vazioTitulo}>{erro}</h3>
                            <Button variant="link" onClick={() => carregar()} className="text-[#7C3AED] font-medium">
                                Tentar novamente
                            </Button>
                        </div>
                    </Card>
                )}

                {!loading && !erro && dados && (
                    <>
                        {/* Hero: composicao da entrega */}
                        <div className={D.detalhes.hero}>
                            <div className={D.detalhes.heroTopo}>
                                <div>
                                    <span className={D.detalhes.heroLabel}>Progresso do envio</span>
                                    <p className={D.detalhes.heroNumero}>
                                        {summary.enviado || 0}
                                        <span className={D.detalhes.heroNumeroSub}>
                                            {` de ${summary.total || 0} ${summary.total === 1 ? 'mensagem enviada' : 'mensagens enviadas'}`}
                                        </span>
                                    </p>
                                </div>
                                <span className={D.detalhes.heroPct}>{`${pctEnviado}%`}</span>
                            </div>

                            <div className={D.detalhes.barra}>
                                {segmentosBarra.map((s) => (
                                    <div
                                        key={s.key}
                                        className={`${D.detalhes.barraSegmento} ${s.classe}`}
                                        style={{ width: `${s.pct}%` }}
                                        title={`${s.label}: ${s.valor}`}
                                    />
                                ))}
                            </div>

                            <div className={D.detalhes.legenda}>
                                {segmentosBarra.map((s) => (
                                    <span key={s.key} className={D.detalhes.legendaItem}>
                                        <span className={`${D.detalhes.legendaDot} ${s.classe}`} />
                                        <span className={D.detalhes.legendaValor}>{s.valor}</span>
                                        {s.label}
                                    </span>
                                ))}
                            </div>

                            <div className={D.detalhes.metaRow}>
                                <span>
                                    {'Início '}
                                    <strong className={D.detalhes.metaForte}>
                                        {formatarDataHora(resumo.primeiro_disparo_em || resumo.data_envio)}
                                    </strong>
                                </span>
                                {emAndamento && resumo.previsao_termino && (
                                    <span>
                                        {'Previsão de término '}
                                        <strong className={D.detalhes.metaForte}>
                                            {formatarHora(resumo.previsao_termino)}
                                        </strong>
                                    </span>
                                )}
                                {!emAndamento && resumo.ultimo_envio_em && (
                                    <span>
                                        {'Último envio '}
                                        <strong className={D.detalhes.metaForte}>
                                            {formatarDataHora(resumo.ultimo_envio_em)}
                                        </strong>
                                    </span>
                                )}
                                {ehSms && resumo.segmentos_total > 0 && (
                                    <span>
                                        <strong className={D.detalhes.metaForte}>{resumo.segmentos_total}</strong>
                                        {` ${resumo.segmentos_total === 1 ? 'crédito consumido' : 'créditos consumidos'}`}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Taxa de entrega: pizza + percentuais (ref. Consumo) */}
                        {summary.total > 0 && chartDataStatus.length > 0 && (
                            <div className={D.detalhes.taxaCard}>
                                <div>
                                    <h3 className={D.detalhes.taxaTitulo}>Taxa de entrega</h3>
                                    <p className={D.detalhes.taxaSub}>
                                        {ehSms
                                            ? 'Enviado, entregue no aparelho e lido — conforme confirmação da operadora'
                                            : 'Enviado, entregue no aparelho e lido — conforme confirmação da Meta'}
                                    </p>
                                </div>
                                <div className={D.detalhes.taxaLayout}>
                                    <div className={D.detalhes.taxaChart}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <defs>
                                                    {chartDataStatus.map((entry, index) => (
                                                        <linearGradient
                                                            key={`grad-camp-${index}`}
                                                            id={`grad-camp-${index}`}
                                                            x1="0"
                                                            y1="0"
                                                            x2="1"
                                                            y2="1"
                                                        >
                                                            <stop offset="0%" stopColor={entry.fill} stopOpacity={1} />
                                                            <stop offset="100%" stopColor={entry.fill} stopOpacity={0.6} />
                                                        </linearGradient>
                                                    ))}
                                                </defs>
                                                <Pie
                                                    data={chartDataStatus}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    outerRadius={70}
                                                    dataKey="value"
                                                    stroke="none"
                                                >
                                                    {chartDataStatus.map((entry, index) => (
                                                        <Cell key={`cell-camp-${index}`} fill={`url(#grad-camp-${index})`} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip
                                                    formatter={(value, name) => [`${value}`, name]}
                                                />
                                                <Legend
                                                    layout="vertical"
                                                    align="right"
                                                    verticalAlign="middle"
                                                    iconSize={8}
                                                    wrapperStyle={{ fontSize: '11px' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className={D.detalhes.taxaRates}>
                                        <div className={D.detalhes.taxaRateEnviado}>
                                            <div className={D.detalhes.taxaRateEnviadoValor}>{`${deliveryRates.sent}%`}</div>
                                            <div className={D.detalhes.taxaRateEnviadoLabel}>Enviado</div>
                                        </div>
                                        <div className={D.detalhes.taxaRateEntregue}>
                                            <div className={D.detalhes.taxaRateEntregueValor}>{`${deliveryRates.delivered}%`}</div>
                                            <div className={D.detalhes.taxaRateEntregueLabel}>Entregue</div>
                                        </div>
                                        <div className={D.detalhes.taxaRateLido}>
                                            <div className={D.detalhes.taxaRateLidoValor}>{`${deliveryRates.read}%`}</div>
                                            <div className={D.detalhes.taxaRateLidoLabel}>Lido</div>
                                        </div>
                                        <div className={D.detalhes.taxaRateErro}>
                                            <div className={D.detalhes.taxaRateErroValor}>{`${deliveryRates.error}%`}</div>
                                            <div className={D.detalhes.taxaRateErroLabel}>Erro</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={D.detalhes.grid}>
                            {/* Coluna principal: destinatarios */}
                            <div className={D.detalhes.card}>
                                <div className={D.detalhes.cardHeader}>
                                    <div className="flex items-center gap-2">
                                        <span className={D.detalhes.cardTitulo}>Destinatários</span>
                                        {dados.paginacao?.total > 0 && (
                                            <span className={D.detalhes.cardBadge}>{dados.paginacao.total}</span>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <Input
                                            value={busca}
                                            onChange={(e) => {
                                                setBusca(e.target.value);
                                                setPagina(1);
                                            }}
                                            placeholder="Nome ou número..."
                                            className="h-8 w-[180px] pr-7 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#1A1D21] rounded-lg"
                                        />
                                        <Iconify
                                            icon="eva:search-outline"
                                            width={14}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-1 flex-wrap px-4 pt-2.5 pb-1">
                                    {FILTROS_STATUS.map((f) => (
                                        <button
                                            key={f.valor || 'todos'}
                                            type="button"
                                            onClick={() => {
                                                setFiltroStatus(f.valor);
                                                setPagina(1);
                                            }}
                                            className={filtroStatus === f.valor ? D.detalhes.filtroChipAtivo : D.detalhes.filtroChip}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>

                                <div className={D.detalhes.cardCorpo}>
                                    {jobs.length === 0 ? (
                                        <div className={D.detalhes.vazio}>
                                            <Iconify icon="eva:inbox-outline" width={24} className="text-gray-300" />
                                            <p className={D.detalhes.vazioTexto}>
                                                {busca || filtroStatus
                                                    ? 'Nenhum destinatário corresponde à busca ou ao filtro.'
                                                    : 'Nenhum destinatário nesta campanha.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            {jobs.map((job) => {
                                                const cfgJob = configStatusJob(job.status);
                                                // provider_status (Entregue/Lido) e preenchido tanto por SMS quanto por
                                                // WhatsApp Business agora — sempre que existir, e mais especifico que o
                                                // status generico do job ("Enviado").
                                                const cfgSms = rotuloSmsStatus(job.provider_status);
                                                const aberto = expandido === job._id;
                                                const podeCancelar = ['agendado', 'processando'].includes(job.status);

                                                return (
                                                    <div key={job._id} className={D.detalhes.destLinha}>
                                                        <div className="min-w-0 flex-1">
                                                            <button
                                                                type="button"
                                                                className="text-left w-full"
                                                                onClick={() => setExpandido(aberto ? null : job._id)}
                                                            >
                                                                <p className={D.detalhes.destNome}>
                                                                    {job.nome_cliente || 'Sem nome'}
                                                                </p>
                                                                <p className={D.detalhes.destNumero}>{job.numero_cliente}</p>
                                                            </button>

                                                            {job.status === 'erro' && job.erro && (
                                                                <p className={D.detalhes.destErro}>{job.erro}</p>
                                                            )}

                                                            {aberto && (
                                                                <div className={D.detalhes.destHistorico}>
                                                                    <p className={D.detalhes.destHistoricoItem}>
                                                                        {`Agendado para ${formatarDataHora(job.data_disparo)}`}
                                                                    </p>
                                                                                    {job.enviado_em && (
                                                                        <p className={D.detalhes.destHistoricoItem}>
                                                                            {`Enviado em ${formatarDataHora(job.enviado_em)}`}
                                                                        </p>
                                                                    )}
                                                                    {job.respondido && (
                                                                        <p className={D.detalhes.destHistoricoItem}>
                                                                            {`Respondeu em ${formatarDataHora(job.respondido_em)}`}
                                                                        </p>
                                                                    )}
                                                                    {(job.status_historico || []).map((h, i) => (
                                                                        // eslint-disable-next-line react/no-array-index-key
                                                                        <p key={i} className={D.detalhes.destHistoricoItem}>
                                                                            {`${rotuloSmsStatus(h.provider_status)?.label || h.provider_status} · ${formatarDataHora(h.em)}`}
                                                                        </p>
                                                                    ))}
                                                                    {job.segmentos > 0 && (
                                                                        <p className={D.detalhes.destHistoricoItem}>
                                                                            {`${job.segmentos} ${job.segmentos === 1 ? 'crédito' : 'créditos'}`}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <span className="hidden md:block w-[100px] text-right text-xs text-gray-400 tabular-nums pt-1 flex-shrink-0">
                                                            {formatarDiaHora(job.enviado_em || job.data_disparo)}
                                                        </span>

                                                        <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                                            {job.respondido && (
                                                                <span className={D.detalhes.destChipRespondido} title={`Respondeu em ${formatarDataHora(job.respondido_em)}`}>
                                                                    <Iconify icon="mdi:reply" width={11} />
                                                                    Respondeu
                                                                </span>
                                                            )}
                                                            {cfgSms ? (
                                                                <span className={`${D.detalhes.destChip} ${cfgSms.classe}`}>
                                                                    {cfgSms.label}
                                                                </span>
                                                            ) : (
                                                                <span className={`${D.detalhes.destChip} ${cfgJob.bg} ${cfgJob.text}`}>
                                                                    {cfgJob.label}
                                                                </span>
                                                            )}
                                                            {podeCancelar && (
                                                                <button
                                                                    type="button"
                                                                    title="Cancelar este envio"
                                                                    onClick={() => handleCancelar(job)}
                                                                    className={D.detalhes.destAcao}
                                                                >
                                                                    <Iconify icon="eva:close-circle-outline" width={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {dados.paginacao?.temMais && (
                                                <button
                                                    type="button"
                                                    onClick={() => setPagina((p) => p + 1)}
                                                    className={D.detalhes.destCarregarMais}
                                                >
                                                    {`Carregar mais (${dados.paginacao.total - jobs.length} restantes)`}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Lateral: mensagem, falhas, linha do tempo, detalhes */}
                            <div className="space-y-4">
                                <div className={D.detalhes.card}>
                                    <div className={D.detalhes.cardHeader}>
                                        <span className={D.detalhes.cardTitulo}>Mensagem enviada</span>
                                    </div>
                                    <div className={`${D.detalhes.cardCorpo} pt-3`}>
                                        {resumo.usaTemplate && resumo.template ? (
                                            <div className={D.detalhes.mensagemTemplateBox}>
                                                <Iconify
                                                    icon="mdi:file-document-outline"
                                                    width={18}
                                                    className="text-[#7C3AED] flex-shrink-0 mt-0.5"
                                                />
                                                <div className="min-w-0">
                                                    <p className={D.detalhes.mensagemTemplateNome}>
                                                        {resumo.template.templateId || resumo.template.id || 'Modelo aprovado'}
                                                    </p>
                                                    <p className={D.detalhes.mensagemTemplateSub}>
                                                        Modelo aprovado pela Meta — o texto final é preenchido por ela
                                                        fora da janela de 24h de conversa.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : resumo.mensagem ? (
                                            <p className={D.detalhes.mensagemTexto}>{resumo.mensagem}</p>
                                        ) : (
                                            <div className={D.detalhes.vazio}>
                                                <Iconify icon="mdi:message-off-outline" width={22} className="text-gray-300" />
                                                <p className={D.detalhes.vazioTexto}>
                                                    Nenhum texto cadastrado para esta campanha.
                                                </p>
                                            </div>
                                        )}

                                        {resumo.anexos?.length > 0 && (
                                            <div className={D.detalhes.anexosLista}>
                                                {resumo.anexos.map((anexo, i) => {
                                                    const nome = anexo.key ? anexo.key.split('/').pop() : `Anexo ${i + 1}`;
                                                    const icone = anexo.type?.startsWith('image')
                                                        ? 'mdi:file-image-outline'
                                                        : anexo.type?.startsWith('audio')
                                                            ? 'mdi:file-music-outline'
                                                            : anexo.type?.startsWith('video')
                                                                ? 'mdi:file-video-outline'
                                                                : 'mdi:file-outline';
                                                    return (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <span key={i} className={D.detalhes.anexoChip} title={nome}>
                                                            <Iconify icon={icone} width={13} />
                                                            <span className="truncate max-w-[140px]">{nome}</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {summary.erro > 0 && (
                                    <div className={D.alertaErro.card}>
                                        <div className={D.alertaErro.header}>
                                            <div className={D.alertaErro.headerIcon}>
                                                <Iconify icon="mdi:alert-circle-outline" width={14} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className={D.alertaErro.headerTitle}>
                                                    {`${summary.erro} ${summary.erro === 1 ? 'envio falhou' : 'envios falharam'}`}
                                                </p>
                                                <p className={D.alertaErro.headerSub}>Motivos mais recentes</p>
                                            </div>
                                        </div>
                                        {comErro.slice(0, 3).map((j) => (
                                            <p key={j._id} className={D.alertaErro.item} title={j.erro}>
                                                {`${j.numero_cliente}: ${j.erro || 'sem detalhe'}`}
                                            </p>
                                        ))}
                                        {comErro.length === 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setFiltroStatus('erro')}
                                                className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
                                            >
                                                Ver os envios que falharam
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className={D.detalhes.card}>
                                    <div className={D.detalhes.cardHeader}>
                                        <span className={D.detalhes.cardTitulo}>Linha do tempo</span>
                                    </div>
                                    <div className={`${D.detalhes.cardCorpo} pt-3`}>
                                        {timeline.length === 0 ? (
                                            <div className={D.detalhes.vazio}>
                                                <Iconify icon="mdi:timeline-clock-outline" width={24} className="text-gray-300" />
                                                <p className={D.detalhes.vazioTexto}>
                                                    Esta campanha ainda não começou a disparar.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className={D.detalhes.timeline}>
                                                {timeline.map((evento) => {
                                                    const cfg = EVENTOS[evento.key];
                                                    return (
                                                        <div key={evento.key} className={D.detalhes.evento}>
                                                            <div className={`${D.detalhes.eventoIcone} ${cfg.classe}`}>
                                                                <Iconify icon={cfg.icon} width={14} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className={D.detalhes.eventoTitulo}>{cfg.label}</p>
                                                                <p className={D.detalhes.eventoMeta}>
                                                                    {evento.detalhes.join(' · ')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={D.detalhes.card}>
                                    <div className={D.detalhes.cardHeader}>
                                        <span className={D.detalhes.cardTitulo}>Detalhes</span>
                                    </div>
                                    <div className={`${D.detalhes.cardCorpo} pt-2`}>
                                        <div className={D.detalhes.infoLinha}>
                                            <span className={D.detalhes.infoLabel}>Canal</span>
                                            <span className={`${D.detalhes.infoValor} flex items-center gap-1`}>
                                                <Iconify icon={cfgTipo.icon} width={13} />
                                                {cfgTipo.label}
                                            </span>
                                        </div>
                                        {cfgPublico && (
                                            <div className={D.detalhes.infoLinha}>
                                                <span className={D.detalhes.infoLabel}>Público</span>
                                                <span className={`${D.detalhes.infoValor} flex items-center gap-1`}>
                                                    <Iconify icon={cfgPublico.icon} width={13} />
                                                    {cfgPublico.label}
                                                </span>
                                            </div>
                                        )}
                                        {resumo.criada_por && (
                                            <div className={D.detalhes.infoLinha}>
                                                <span className={D.detalhes.infoLabel}>Criada por</span>
                                                <span className={D.detalhes.infoValor}>{resumo.criada_por}</span>
                                            </div>
                                        )}
                                        {resumo.criada_em && (
                                            <div className={D.detalhes.infoLinha}>
                                                <span className={D.detalhes.infoLabel}>Criada em</span>
                                                <span className={D.detalhes.infoValor}>{formatarDataHora(resumo.criada_em)}</span>
                                            </div>
                                        )}
                                        <div className={D.detalhes.infoLinha}>
                                            <span className={D.detalhes.infoLabel}>Agendada para</span>
                                            <span className={D.detalhes.infoValor}>{formatarDataHora(resumo.data_envio)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
